import { IHeaderParams, SortDirection } from '@ag-grid-community/core';

import { useState, useEffect, MouseEvent } from 'react';

import { Box, Tooltip, InfoOutlinedIcon, BodyXS, UnfoldMoreIcon, ArrowUpwardIcon, ArrowDownwardIcon, IconButton } from '@parspec/pixel';
import FilterListIcon from '@mui/icons-material/FilterList';

interface ICustomHeaderParams extends IHeaderParams {
    tooltipText: string;
    enableSorting: boolean;
    setSort: (value: SortDirection) => void;
    column: any;
    width?: string;
    enableFiltering?: boolean;
    justifyHeaderContent?: string;
}

export const CustomTableHeader = (props: ICustomHeaderParams) => {
    const { tooltipText, enableSorting, setSort, column, width, enableFiltering = false, displayName, justifyHeaderContent } = props;

    const [currSortState, setCurrSortState] = useState<string | null | undefined>(null);
    const [isFiltered, setIsFiltered] = useState(false);

    const onSortChanged = () => {
        if (column.isSortAscending()) {
            setCurrSortState('asc');
        } else if (column.isSortDescending()) {
            setCurrSortState('desc');
        } else {
            setCurrSortState(null);
        }
    };

    const onSortRequested = () => {
        const currSort = column?.sort;

        let newSort;

        if (currSort === 'asc') {
            newSort = 'desc';
        } else if (currSort === 'desc') {
            newSort = null;
        } else {
            newSort = 'asc';
        }
        setSort(newSort as SortDirection);
        setCurrSortState(newSort);
    };

    useEffect(() => {
        column.addEventListener('sortChanged', onSortChanged);
        onSortChanged();
    }, []);

    const handleOnClikedFilter = (e: MouseEvent<HTMLButtonElement>) => {
        props.showColumnMenu(e.currentTarget);
    };

    useEffect(() => {
        const filterChangedListener = () => {
            setIsFiltered(column.isFilterActive());
        };

        // Check if filter is active on mount
        // Doing this because if user scrolls the column outside the viewport
        // The column gets destroyed and the local state is lost
        setIsFiltered(column.isFilterActive());

        // Add event listener for filter changes
        column.addEventListener('filterChanged', filterChangedListener);

        // Cleanup event listener on unmount
        return () => {
            column.removeEventListener('filterChanged', filterChangedListener);
        };
    }, [column]);

    return (
        <Box mt={1} height="100%" display="flex" justifyContent={justifyHeaderContent || "space-between"} alignItems="center" width={1}>
            <Box display="flex" alignItems="center">
                <BodyXS fontWeight={500}>{displayName}</BodyXS>
                {enableSorting && (
                    <Box pl={'6px'} sx={{ cursor: 'pointer' }} onClick={() => onSortRequested()}>
                        {(currSortState === undefined || currSortState === null) && (
                            <Box color={'text.secondary'}>
                                <UnfoldMoreIcon color={'inherit'} fontSize="small" />
                            </Box>
                        )}
                        {currSortState === 'asc' && (
                            <Box color={'text.secondary'}>
                                <ArrowUpwardIcon color={'inherit'} fontSize="small" />
                            </Box>
                        )}
                        {currSortState === 'desc' && (
                            <Box color={'text.secondary'}>
                                <ArrowDownwardIcon color={'inherit'} fontSize="small" />
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
            <Box display="flex" gap={1} alignItems="center">
                {tooltipText && (
                    <Box>
                        <Tooltip placement="top" title={<span style={{ display: 'block', textAlign: 'center', width: width ? width : '150px' }}>{tooltipText}</span>}>
                            <Box color={'text.secondary'}>
                                <InfoOutlinedIcon color={'inherit'} fontSize="small" />
                            </Box>
                        </Tooltip>
                    </Box>
                )}

                {enableFiltering && (
                    <Box>
                        <IconButton sx={{ pb: 3 }} onClick={handleOnClikedFilter}>
                            {isFiltered ? <FilterListIcon fontSize="small" sx={{ bgcolor: 'primary.light', color: 'primary.main', borderRadius: '2px' }} /> : <FilterListIcon fontSize="small" />}
                        </IconButton>
                    </Box>
                )}
            </Box>
        </Box>
    );
};
